import React, { useState } from "react";
import {
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ExternalLink,
  FileText,
  GitPullRequest,
  Rocket,
  Target,
  Folder,
} from "lucide-react";
import { TraceabilityData, TraceabilityStage } from "../../types";

interface TraceabilityFlowProps {
  traceability: TraceabilityData;
  className?: string;
}

const TraceabilityFlow: React.FC<TraceabilityFlowProps> = ({
  traceability,
  className = "",
}) => {
  const [selectedStage, setSelectedStage] = useState<string | null>(null);

  const stages = [
    {
      key: "spec",
      title: "Specification",
      description: "Initial requirements and specifications",
      icon: Target,
      data: traceability.spec,
    },
    {
      key: "pseudocode",
      title: "Pseudocode",
      description: "Generated pseudocode from specifications",
      icon: FileText,
      data: traceability.pseudocode,
    },
    {
      key: "tasks",
      title: "Task Breakdown",
      description: "Tasks broken down from pseudocode",
      icon: CheckCircle,
      data: traceability.tasks,
    },
    {
      key: "workspace",
      title: "Workspace",
      description: "Workspace setup from tasks",
      icon: Folder,
      data: traceability.workspace,
    },
    {
      key: "pr",
      title: "Pull Request",
      description: "Code review and pull request",
      icon: GitPullRequest,
      data: traceability.pr,
    },
    {
      key: "deployment",
      title: "Deployment",
      description: "Production deployment",
      icon: Rocket,
      data: traceability.deployment,
    },
  ];

  const getStatusIcon = (status: TraceabilityStage["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "in_progress":
        return <Clock className="w-5 h-5 text-blue-600" />;
      case "failed":
        return <XCircle className="w-5 h-5 text-red-600" />;
      case "pending":
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: TraceabilityStage["status"]) => {
    switch (status) {
      case "completed":
        return "border-green-500 bg-green-50 text-green-800";
      case "in_progress":
        return "border-blue-500 bg-blue-50 text-blue-800";
      case "failed":
        return "border-red-500 bg-red-50 text-red-800";
      case "pending":
      default:
        return "border-gray-300 bg-gray-50 text-gray-600";
    }
  };

  const getConnectorColor = (
    currentStatus: TraceabilityStage["status"],
    nextStatus?: TraceabilityStage["status"]
  ) => {
    if (currentStatus === "completed") {
      return "border-green-500";
    } else if (currentStatus === "in_progress") {
      return "border-blue-500";
    } else if (currentStatus === "failed") {
      return "border-red-500";
    } else {
      return "border-gray-300";
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStageProgress = () => {
    const completedStages = stages.filter(
      (stage) => stage.data.status === "completed"
    ).length;
    return (completedStages / stages.length) * 100;
  };

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      {/* Header */}
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Traceability Flow
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Complete journey from specification to deployment
        </p>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>Overall Progress</span>
            <span>{Math.round(getStageProgress())}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getStageProgress()}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Flow Visualization */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {stages.map((stage, index) => {
            const isSelected = selectedStage === stage.key;
            const isLast = index === stages.length - 1;
            const StageIcon = stage.icon;

            return (
              <div key={stage.key} className="relative">
                {/* Stage Card */}
                <div
                  onClick={() =>
                    setSelectedStage(isSelected ? null : stage.key)
                  }
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                    isSelected ? "ring-2 ring-blue-500 ring-opacity-50" : ""
                  } ${getStatusColor(stage.data.status)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
                        <StageIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-gray-900">
                            {stage.title}
                          </h4>
                          {getStatusIcon(stage.data.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {stage.description}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            {stage.data.timestamp &&
                              formatTimestamp(stage.data.timestamp)}
                          </div>

                          <div className="flex items-center space-x-2">
                            {stage.data.url && (
                              <a
                                href={stage.data.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                title="View details"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                stage.data.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : stage.data.status === "in_progress"
                                  ? "bg-blue-100 text-blue-800"
                                  : stage.data.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {stage.data.status
                                .replace("_", " ")
                                .toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isSelected && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-900">
                            Status:
                          </span>
                          <div className="mt-1 capitalize">
                            {stage.data.status.replace("_", " ")}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">
                            Timestamp:
                          </span>
                          <div className="mt-1">
                            {stage.data.timestamp
                              ? formatTimestamp(stage.data.timestamp)
                              : "Not started"}
                          </div>
                        </div>
                      </div>

                      {stage.data.url && (
                        <div className="mt-3">
                          <a
                            href={stage.data.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            View Details
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Connector */}
                {!isLast && (
                  <div className="flex justify-center my-2">
                    <div
                      className={`w-0.5 h-6 border-l-2 border-dashed ${getConnectorColor(
                        stage.data.status,
                        stages[index + 1]?.data.status
                      )}`}
                    ></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="p-6 border-t bg-gray-50">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-green-600">
              {stages.filter((s) => s.data.status === "completed").length}
            </div>
            <div className="text-xs text-gray-600">Completed</div>
          </div>
          <div>
            <div className="text-lg font-bold text-blue-600">
              {stages.filter((s) => s.data.status === "in_progress").length}
            </div>
            <div className="text-xs text-gray-600">In Progress</div>
          </div>
          <div>
            <div className="text-lg font-bold text-red-600">
              {stages.filter((s) => s.data.status === "failed").length}
            </div>
            <div className="text-xs text-gray-600">Failed</div>
          </div>
          <div>
            <div className="text-lg font-bold text-gray-600">
              {stages.filter((s) => s.data.status === "pending").length}
            </div>
            <div className="text-xs text-gray-600">Pending</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraceabilityFlow;
